/**
 * 文章数据层 · 方案 B（运行时 fetch）
 *
 * 设计目标：上线后加文章不需要 rebuild
 *  - manifest.json 列出所有文章 meta（构建时由 scripts/build-articles-manifest.mjs 生成）
 *  - 列表页 fetch manifest.json
 *  - 单文章页才 fetch 对应的 .md（按需，省流量）
 *  - 部署时只需 scp 一个 md + 重跑 manifest 脚本 + scp manifest.json
 *
 * 实现：单例缓存。manifest 全局拉一次；md 单文章按 slug 缓存。
 */

import type { LifeStage } from '@/domain/stage'

export type ArticleStage = LifeStage

export type ArticleCategory =
  | 'before_exam'   // 03 考前必读
  | 'high_school'   // 04 高中适应预警
  | 'summer_class'  // 05 假期报班必读

export const CATEGORY_LABEL: Record<ArticleCategory, { num: string; title: string; subtitle: string }> = {
  before_exam: {
    num: '03',
    title: '考前注意事项',
    subtitle: '姜博士专栏 · 中考前 30 天，帮孩子稳稳走进考场'
  },
  high_school: {
    num: '04',
    title: '高中学习预警',
    subtitle: '录取后的关键准备 · 高一前 3 个月最常见的"坑"'
  },
  summer_class: {
    num: '05',
    title: '暑期预习安排',
    subtitle: '怎么挑暑期班 · 怎么安排假期 · 怎么让"预习"真的有效'
  }
}

export interface ArticleMeta {
  slug: string
  title: string
  subtitle?: string
  category: ArticleCategory
  stage?: ArticleStage[]
  order: number
  free: boolean
  author: string
  author_title: string
  published_at: string
  read_minutes: number
  cover_emoji: string
  tags: string[]
  hero_quote?: string
  source_note?: string
  /** manifest 自动注入：原始 md 文件名（用于 fetch） */
  _file: string
}

export interface Article {
  meta: ArticleMeta
  body: string
}

// ──────────────── 网络层 ────────────────

const BASE = '/articles'
const MANIFEST_URL = `${BASE}/manifest.json`

let manifestPromise: Promise<ArticleMeta[]> | null = null
const articleCache = new Map<string, Promise<Article | null>>()

export function loadManifest(): Promise<ArticleMeta[]> {
  if (!manifestPromise) {
    manifestPromise = fetch(MANIFEST_URL, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`manifest fetch ${r.status}`)
        return r.json()
      })
      .then((j: { articles: ArticleMeta[] }) => {
        const list = Array.isArray(j?.articles) ? j.articles : []
        list.sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        return list
      })
      .catch((e) => {
        console.error('[articles] loadManifest failed:', e)
        manifestPromise = null
        return []
      })
  }
  return manifestPromise
}

export async function loadArticle(slug: string): Promise<Article | null> {
  if (articleCache.has(slug)) return articleCache.get(slug)!

  const promise = (async () => {
    const list = await loadManifest()
    const meta = list.find((m) => m.slug === slug)
    if (!meta) return null
    const r = await fetch(`${BASE}/${meta._file}`, { cache: 'default' })
    if (!r.ok) {
      console.error(`[articles] fetch md ${slug} → ${r.status}`)
      return null
    }
    const raw = await r.text()
    const body = stripFrontmatter(raw)
    return { meta, body }
  })()

  articleCache.set(slug, promise)
  return promise
}

function stripFrontmatter(raw: string): string {
  if (!raw.startsWith('---')) return raw
  const end = raw.indexOf('\n---', 3)
  if (end === -1) return raw
  return raw.slice(end + 4).replace(/^\s*\n/, '')
}

// ──────────────── 便利 API ────────────────

export async function listArticles(): Promise<ArticleMeta[]> {
  return loadManifest()
}

export async function listArticlesByCategory(category: ArticleCategory): Promise<ArticleMeta[]> {
  const all = await loadManifest()
  return all.filter((a) => a.category === category)
}

export async function listArticlesByStage(stage: ArticleStage): Promise<ArticleMeta[]> {
  const all = await loadManifest()
  return all.filter((a) => a.stage?.includes(stage))
}
